import { AudioLines, GalleryVertical, Plus } from "lucide-react";
import { FC, useMemo, useState } from "react";

import BuilderIcon from "@/assets/logos/builder.svg?react";
import { useUpdateSKUMutation } from "@/data/mutations";
import { SKUPageContentTabType } from "@/data/types";
import { addTab, CREATABLE_TAB_TYPES, MAX_TABS } from "@/lib/cms";

import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { useCMSContext } from "./cms-context";

export const CMSAddTabButton = () => {
  const [isOpen, setOpen] = useState(false);
  const { sku, pageContent } = useCMSContext();

  const updateSKUMutation = useUpdateSKUMutation(sku.id);

  const handleAddTab = async (type: SKUPageContentTabType) => {
    await updateSKUMutation.mutateAsync({
      page_content: addTab(pageContent, type),
    });

    setOpen(false);
  };

  const canAddTab = useMemo(() => {
    if (updateSKUMutation.isPending) {
      return false;
    }

    const total = pageContent.data?.length ?? 0;
    return total < MAX_TABS;
  }, [pageContent.data?.length, updateSKUMutation.isPending]);

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className="mt-6"
          disabled={!canAddTab || updateSKUMutation.isPending}
        >
          <Plus className="size-4" />
          Add tab
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className="w-90">
        <div>
          <h4 className="font-medium text-base">Add tab</h4>
          <p className="text-sm text-muted-foreground">
            Choose a type of tab to add
          </p>
          <div className="space-y-2 mt-2">
            {CREATABLE_TAB_TYPES.map((type) => (
              <TabPickerButton key={type} type={type} onClick={handleAddTab} />
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface TabPickerButtonProps {
  type: SKUPageContentTabType;
  onClick: (type: SKUPageContentTabType) => void;
}

export const TabPickerButton: FC<TabPickerButtonProps> = (props) => {
  if (props.type === SKUPageContentTabType.PRODUCT_TAB) {
    return null;
  }

  return (
    <Button
      variant="outline"
      className="w-full justify-start"
      onClick={() => props.onClick(props.type)}
    >
      {TabPickerTypeMetadata[props.type].icon}
      {TabPickerTypeMetadata[props.type].title}
      <span className="text-muted-foreground font-normal -ml-1">
        {TabPickerTypeMetadata[props.type].description}
      </span>
    </Button>
  );
};

const TabPickerTypeMetadata = {
  [SKUPageContentTabType.MUSIC_TAB]: {
    title: "Audio",
    description: "Insert mp3 files to make a playlist",
    icon: <AudioLines className="size-4" />,
  },
  [SKUPageContentTabType.GALLERY_TAB]: {
    title: "Gallery",
    description: "Add images to a gallery",
    icon: <GalleryVertical className="size-4" />,
  },
  [SKUPageContentTabType.BUILDER_IO_TAB]: {
    title: "Builder.io",
    description: "Plug in a page built in Builder.io",
    icon: <BuilderIcon className="size-4" />,
  },
} as const;
